import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Key, Lock, Check, ChevronDown, Search, Shield, Info } from 'lucide-react';
import { useRoles } from '@/store/role';
import { useResources } from '@/store/resource';
import { usePermissions } from '@/store/permission';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { useLayout } from '@/contexts/layout-context';

export const CreateRolePage = () => {
    const [key, setKey] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [resourceKindId, setResourceKindId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const createRole = useRoles((state) => state.create);
    const grantPermission = useRoles((state) => state.grantPermission);
    const { resourceKinds, listResourceKinds } = useResources();
    const { permissions, list: listPermissions } = usePermissions();
    
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    useEffect(() => {
        setBreadcrumbs([{ name: 'Roles', to: '/m/roles' }, { name: 'Create' }]);
        listResourceKinds().catch(console.error);
        listPermissions().catch(console.error);
    }, [setBreadcrumbs, listResourceKinds, listPermissions]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.searchable-select-container')) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    // Filter permissions for the selected resource kind
    const availablePermissions = permissions.filter(
        (p) => p.resource_kind_id === resourceKindId
    );

    const togglePermission = (permId: string) => {
        if (selectedPermissions.includes(permId)) {
            setSelectedPermissions(selectedPermissions.filter((id) => id !== permId));
        } else {
            setSelectedPermissions([...selectedPermissions, permId]);
        }
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!resourceKindId) {
            toast.error('Please select a Resource Kind');
            return;
        }
        if (!key) {
            toast.error('Please enter a Role Key');
            return;
        }
        if (!name) {
            toast.error('Please enter a Role Name');
            return;
        }

        setIsSubmitting(true);
        try {
            const fullRoleId = `${resourceKindId}::${key}`;
            
            // 1. Create the role
            await createRole({
                id: fullRoleId,
                name,
                description: description || undefined,
                applicable_scopes: [resourceKindId],
            });

            // 2. Associate selected permissions
            if (selectedPermissions.length > 0) {
                for (const permId of selectedPermissions) {
                    await grantPermission(fullRoleId, permId);
                }
            }

            toast.success(`Role "${name}" created successfully`);
            navigate('/m/roles');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create role');
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectedKind = resourceKinds.find((k) => k.id === resourceKindId);
    const displayValue = isOpen
        ? searchQuery
        : selectedKind
        ? `${selectedKind.name} (${selectedKind.id})`
        : '';
        
    const filteredKinds = resourceKinds.filter(
        (k) =>
            k.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            k.id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-4 lg:p-8 max-w-4xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title="Create Role"
                subtitle="Define a new role and grant fine-grained permissions for a specific resource kind."
                actions={
                    <Button variant="ghost" onClick={() => navigate('/m/roles')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                }
            />

            <div className="mt-8 animate-in slide-in-from-bottom-4 duration-500">
                <Card
                    title="Role Identity & Permissions"
                    subtitle="Configure role details and assign resource kind permissions."
                    className="glass"
                >
                    <form onSubmit={submit} className="space-y-8">
                        {/* 1. Resource Kind Selection at the top */}
                        <div className="space-y-2 relative searchable-select-container">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                Resource Kind <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white w-full cursor-pointer"
                                    placeholder="Select resource kind..."
                                    value={displayValue}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        if (!isOpen) setIsOpen(true);
                                    }}
                                    onFocus={() => setIsOpen(true)}
                                />
                                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                                    <Search className="w-4 h-4" />
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsOpen(!isOpen);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                >
                                    <ChevronDown
                                        className={`w-4 h-4 transition-transform duration-200 ${
                                            isOpen ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>
                            </div>

                            {isOpen && (
                                <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-900">
                                    {filteredKinds.length > 0 ? (
                                        filteredKinds.map((k) => (
                                            <div
                                                key={k.id}
                                                onClick={() => {
                                                    setResourceKindId(k.id);
                                                    setIsOpen(false);
                                                    setSearchQuery('');
                                                    setSelectedPermissions([]); // Reset selected permissions
                                                }}
                                                className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-955/20 hover:text-orange-600 transition-colors flex flex-col ${
                                                    resourceKindId === k.id
                                                        ? 'bg-orange-50/50 dark:bg-orange-955/10 text-orange-600 font-medium'
                                                        : 'text-slate-700 dark:text-slate-300'
                                                }`}
                                            >
                                                <span className="font-semibold text-slate-900 dark:text-white">
                                                    {k.name}
                                                </span>
                                                <span className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 font-mono">
                                                    {k.id}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-4 py-3 text-sm text-slate-400 dark:text-slate-500 text-center">
                                            No resource kinds match your search
                                        </div>
                                    )}
                                </div>
                            )}
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                Scope this role's permissions to a specific resource kind.
                            </p>
                        </div>

                        {/* 2. Key, Name and Description below it, disabled until Resource Kind is selected */}
                        <div className="space-y-6">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Role Key <span className="text-red-500">*</span>
                                </label>
                                <div className={`flex rounded-lg border overflow-hidden transition-all duration-200 ${
                                    !resourceKindId 
                                        ? 'border-slate-100 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-950/20' 
                                        : 'border-slate-200 dark:border-slate-700 focus-within:border-orange-500 focus-within:ring-4 focus-within:ring-orange-500/10'
                                }`}>
                                    {resourceKindId && (
                                        <span className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-r border-slate-200 dark:border-slate-700 px-3 py-2 text-sm flex items-center font-mono select-none">
                                            {resourceKindId}::
                                        </span>
                                    )}
                                    <div className="relative flex-1 flex items-center">
                                        <div className="absolute left-3 text-slate-400">
                                            <Key className="w-4 h-4" />
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-4 py-2 bg-transparent text-sm outline-none dark:text-white dark:placeholder-slate-500 disabled:text-slate-400 cursor-text"
                                            placeholder={resourceKindId ? "e.g. administrator" : "Select Resource Kind first..."}
                                            value={key}
                                            onChange={(e) => setKey(e.target.value)}
                                            required
                                            disabled={!resourceKindId}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {!resourceKindId 
                                        ? "Please select a Resource Kind first." 
                                        : "Enter the operation key (typically lowercase, e.g. admin, viewer, operator)."
                                    }
                                </p>
                            </div>

                            <InputField
                                label="Role Name"
                                placeholder={resourceKindId ? "e.g. Administrator" : "Select Resource Kind first..."}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                icon={<Shield className="w-4 h-4" />}
                                disabled={!resourceKindId}
                                required
                            />

                            <InputField
                                label="Brief Description"
                                placeholder={resourceKindId ? "What access privileges does this role grant?" : "Select Resource Kind first..."}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                icon={<Lock className="w-4 h-4" />}
                                disabled={!resourceKindId}
                            />
                        </div>

                        {/* 3. Permissions Checklist */}
                        {resourceKindId && (
                            <div className="pt-6 border-t border-slate-100 dark:border-slate-800 space-y-4">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">Role Permissions</h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        Select the permissions from resource kind <code className="font-mono">{resourceKindId}</code> that this role should include.
                                    </p>
                                </div>

                                {availablePermissions.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto p-1 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50/50 dark:bg-slate-950/20">
                                        {availablePermissions.map((perm) => (
                                            <div
                                                key={perm.id}
                                                onClick={() => togglePermission(perm.id)}
                                                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                    selectedPermissions.includes(perm.id)
                                                        ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-500/10'
                                                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPermissions.includes(perm.id)}
                                                    onChange={() => {}}
                                                    className="w-4 h-4 rounded text-orange-500 focus:ring-orange-500 border-slate-300 dark:border-slate-700 mt-0.5"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm font-bold text-slate-900 dark:text-white block truncate">
                                                        {perm.name}
                                                    </span>
                                                    <span className="text-[10px] text-slate-500 dark:text-slate-300 font-mono block truncate mt-0.5">
                                                        {perm.id.split('::').pop()}
                                                    </span>
                                                    {perm.description && (
                                                        <span className="text-[10px] text-slate-600 dark:text-slate-400 block truncate mt-1">
                                                            {perm.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-6 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-lg text-slate-500 text-sm flex flex-col items-center gap-2">
                                        <Info className="w-5 h-5 text-slate-400" />
                                        <span>No permissions defined for Resource Kind <code className="font-mono">{resourceKindId}</code> yet.</span>
                                        <span className="text-xs text-slate-400">You must define permissions under this resource kind first.</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="pt-8 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                            <Button variant="ghost" type="button" onClick={() => navigate('/m/roles')}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                isLoading={isSubmitting}
                                disabled={!resourceKindId || !key || !name}
                                className="min-w-40 gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Create Role
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};
